import { useGetScopedAccountHierarchy } from 'actions/accounts';
import { useGetAccountAssessmentTemplates } from 'actions/envScales/accountAssessmentTemplates';
import { useGetAllObservers } from 'actions/observers';
import { useMemo } from 'react';
import { SelectedAccount } from 'types/api/Account';
import FilterElement from './FilterElement';
import centerIcon from 'images/envScales/centerIcon.svg';
import observerIcon from 'images/envScales/observerIcon.svg';
import templateIcon from 'images/envScales/templateIcon.svg';
import calendarIcon from 'images/envScales/calendarInProgressIcon.svg';
import ageLevelIcon from 'images/envScales/ageLevelIcon.svg';
import { getAgeLevel } from 'helpers/ageLevel';
import { useMediaQuery } from 'react-responsive';
import i18n from 'helpers/i18n';
import messages from './messages';

type FilterType = {
  age_level?: 'Inf' | 'Todd' | 'Pre-K' | 'PK-3rd' | '';
  assessment_template_id?: string;
  observer_guids?: string[];
  node_ids?: number[];
  start_date?: string;
  end_date?: string;
  informal?: boolean;
};

interface FilterCatalogProps {
  filters: FilterType;
  account?: SelectedAccount;
  onDelete: (filter: string) => void;
}

function FiltersCatalog({ account, filters, onDelete }: FilterCatalogProps) {
  const accountGuid = account === undefined ? '' : account.guid;
  const currentAccountId = account === undefined ? 0 : account.id;
  const { data: templatesData, isSuccess: hasTemplatesData } =
    useGetAccountAssessmentTemplates(accountGuid);
  const { data: hierarchyData, isSuccess: hasHierarchyData } =
    useGetScopedAccountHierarchy(currentAccountId);
  const { data: observersData } = useGetAllObservers(currentAccountId);
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const hasAssessmentTemplateFilter = filters.assessment_template_id !== '';
  const hasAgeLevelFilter = filters.age_level !== '';
  const hasObserverFilter = Boolean(filters.observer_guids?.length);
  const hasNodeFilter = Boolean(filters.node_ids?.length);
  const hasStartDateFilter = filters.start_date !== '';
  const hasEndDateFilter = filters.end_date !== '';
  const hasInformalFilter = filters.informal !== undefined;

  const numOfFilters = useMemo(() => {
    let count = 0;

    if (hasAssessmentTemplateFilter) count += 1;
    if (hasAgeLevelFilter) count += 1;
    if (hasInformalFilter) count += 1;
    if (hasObserverFilter) count += 1;
    if (hasNodeFilter) count += 1;
    if (hasStartDateFilter) count += 1;
    if (hasEndDateFilter) count += 1;

    return count;
  }, [
    hasAssessmentTemplateFilter,
    hasAgeLevelFilter,
    hasInformalFilter,
    hasObserverFilter,
    hasNodeFilter,
    hasStartDateFilter,
    hasEndDateFilter
  ]);

  const hasFiltersForMobile = numOfFilters > 0;

  const templateName = useMemo(() => {
    if (!hasAssessmentTemplateFilter || !hasTemplatesData || !templatesData) {
      return '';
    }

    const templateNameById = new Map<string, string>(
      templatesData.account_assessment_templates.map((template: any) => [
        String(template.assessment_template.id),
        String(template.assessment_template.name)
      ])
    );

    return templateNameById.get(String(filters.assessment_template_id)) || '';
  }, [
    hasAssessmentTemplateFilter,
    hasTemplatesData,
    templatesData,
    filters.assessment_template_id
  ]);

  const ageLevelName = filters.age_level && getAgeLevel(filters.age_level).name;

  const observersName = useMemo(() => {
    if (!hasObserverFilter || !observersData) {
      return '';
    }

    const observerNameByGuid = new Map(
      observersData.map((observer: any) => [observer.guid, observer.name])
    );

    return (filters.observer_guids || [])
      .map(guid => observerNameByGuid.get(guid) || '')
      .filter(Boolean)
      .join(', ');
  }, [hasObserverFilter, observersData, filters.observer_guids]);

  const hierarchyName = useMemo(() => {
    if (!hasNodeFilter || !hasHierarchyData || !hierarchyData) {
      return '';
    }

    const hierarchyNodes = Array.isArray(hierarchyData?.hierarchy)
      ? hierarchyData?.hierarchy
      : hierarchyData?.hierarchy?.children || [];

    const nodes = [...hierarchyNodes];
    const hierarchyNameById = new Map<number, string>();

    while (nodes.length) {
      const node = nodes.pop();
      if (!node) {
        continue;
      }

      hierarchyNameById.set(node.id, node.name);

      if (node.children?.length) {
        nodes.push(...node.children);
      }
    }

    return (filters.node_ids || [])
      .map(id => hierarchyNameById.get(id) || '')
      .filter(Boolean)
      .join(', ');
  }, [hasNodeFilter, hasHierarchyData, hierarchyData, filters.node_ids]);

  return !isTablet ? (
    <div className="flex flex-wrap gap-2 mt-3">
      {hasAssessmentTemplateFilter && (
        <FilterElement
          icon={templateIcon}
          name={templateName}
          onDelete={() => onDelete('assessment_template_id')}
        />
      )}

      {hasAgeLevelFilter && (
        <FilterElement
          icon={ageLevelIcon}
          name={ageLevelName!}
          onDelete={() => onDelete('age_level')}
        />
      )}

      {hasInformalFilter && (
        <FilterElement
          icon={ageLevelIcon}
          name={
            filters.informal
              ? i18n.ft(messages.informal)
              : i18n.ft(messages.formal)
          }
          onDelete={() => onDelete('informal')}
        />
      )}

      {hasObserverFilter ? (
        <FilterElement
          icon={observerIcon}
          name={observersName!}
          onDelete={() => onDelete('observer_guids')}
        />
      ) : null}

      {hasNodeFilter ? (
        <FilterElement
          icon={centerIcon}
          name={hierarchyName!}
          onDelete={() => onDelete('node_ids')}
        />
      ) : null}

      {hasStartDateFilter && hasEndDateFilter ? (
        <FilterElement
          icon={calendarIcon}
          name={filters.start_date!}
          secondName={filters.end_date}
          onDelete={() => onDelete('end_date')}
        />
      ) : hasStartDateFilter ? (
        <FilterElement
          icon={calendarIcon}
          name={filters.start_date!}
          onDelete={() => onDelete('start_date')}
        />
      ) : hasEndDateFilter ? (
        <FilterElement
          icon={calendarIcon}
          name=""
          secondName={filters.end_date}
          onDelete={() => onDelete('end_date')}
        />
      ) : null}
    </div>
  ) : (
    <div className="flex mt-3">
      {hasFiltersForMobile && (
        <FilterElement
          name={`${i18n.ft(messages.filters)} (${numOfFilters})`}
          onDelete={() => onDelete('all')}
        />
      )}
    </div>
  );
}

export default FiltersCatalog;
